import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SkipLink } from '../components/SkipLink';
import { toast } from 'sonner';
import { Send, CheckCircle2, MessageSquare, ArrowLeft, Sparkles } from 'lucide-react';
import { VoiceMode } from '../components/VoiceMode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DemoChat = () => {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const fetchMessages = useCallback(async (sessionId) => {
    try {
      const res = await axios.get(`${API}/demo/sessions/${sessionId}/messages`);
      if (res.data.messages) {
        setMessages(res.data.messages);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  }, []);

  const sendMessage = useCallback(async (sessionId, text) => {
    try {
      const res = await axios.post(`${API}/demo/sessions/${sessionId}/chat`, { content: text });
      await fetchMessages(sessionId);
      if (res.data.session_status === 'completed') {
        setSession(prev => ({ ...prev, status: 'completed' }));
      }
      if (res.data.session_status === 'paused') {
        setSession(prev => ({ ...prev, status: 'paused' }));
      }
      return res.data;
    } catch (err) {
      console.error('Chat error:', err);
      toast.error('Failed to send message');
      return null;
    }
  }, [fetchMessages]);

  useEffect(() => {
    const initDemo = async () => {
      try {
        const res = await axios.post(`${API}/demo/start`);
        const s = res.data;
        setSession(s);
        sessionRef.current = s;
        setStarting(false);

        setSending(true);
        await sendMessage(s.id, "Hi! I'd like to try filling out this demo form.");
        setSending(false);
        inputRef.current?.focus();
      } catch (err) {
        console.error('Failed to start demo:', err);
        toast.error('Could not start demo');
        setStarting(false);
      }
    };
    initDemo();
  }, [sendMessage]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending || !session) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    setMessages(prev => [...prev, { id: `temp-${Date.now()}`, role: 'user', content: text }]);
    await sendMessage(session.id, text);
    setSending(false);
    inputRef.current?.focus();
  };

  if (starting) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <SkipLink />
        <div className="text-center" role="status" aria-live="polite">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3B82F6] mx-auto" aria-hidden="true" />
          <p className="mt-4 text-[#94A3B8]">Starting demo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col" data-testid="demo-chat-page">
      <SkipLink />

      {/* Header */}
      <header className="border-b border-[#1E293B] px-4 py-4 bg-[#0F172A]" role="banner">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to="/" className="text-[#94A3B8] hover:text-white transition-colors" aria-label="Back to homepage">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="w-10 h-10 bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] rounded-full flex items-center justify-center" aria-hidden="true">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white text-lg" data-testid="demo-form-title" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Demo: Customer Feedback
              </h1>
              <p className="text-xs text-[#94A3B8]">Try our conversational form experience</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs px-3 py-1 rounded-full bg-[#1E293B] text-[#3B82F6] font-medium border border-[#3B82F6]/30" data-testid="demo-badge">
              DEMO
            </span>
            {session?.status === 'completed' && (
              <span className="flex items-center text-xs text-emerald-400 space-x-1" role="status" data-testid="demo-completed-badge">
                <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                <span>Done</span>
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Messages */}
      <main id="main-content" className="flex-1 overflow-y-auto px-4 py-8" role="log" aria-label="Demo conversation">
        <div className="max-w-2xl mx-auto space-y-4">
          <AnimatePresence>
            {messages.map((msg, i) => (
              <motion.div
                key={msg.id || i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`demo-message-${msg.role}-${i}`}
              >
                <div
                  className={`max-w-[80%] px-5 py-3 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#3B82F6] text-white rounded-br-sm'
                      : 'bg-[#1E293B] text-[#E2E8F0] border border-[#334155] rounded-bl-sm'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {sending && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start" role="status">
              <div className="bg-[#1E293B] border border-[#334155] rounded-2xl rounded-bl-sm px-5 py-3">
                <div className="flex space-x-2" aria-hidden="true">
                  <div className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="sr-only">AI is thinking</span>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input */}
      <div className="sticky bottom-0 bg-[#0F172A] border-t border-[#1E293B] px-4 py-4">
        <div className="max-w-2xl mx-auto">
          {session?.status === 'completed' ? (
            <div className="text-center space-y-3" data-testid="demo-completed-cta">
              <p className="text-[#94A3B8] text-sm">Demo complete! Ready to use Evenly Respond for your own forms?</p>
              <Link to="/auth">
                <Button className="rounded-full bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] hover:opacity-90 px-8 text-sm" data-testid="demo-signup-button">
                  Get Started Free
                </Button>
              </Link>
            </div>
          ) : session?.status === 'paused' ? (
            <div className="text-center space-y-3" data-testid="demo-paused-cta">
              <p className="text-[#94A3B8] text-sm">Session paused. Your progress is saved!</p>
              <div className="flex justify-center gap-3">
                <Button
                  onClick={() => { setSession(prev => ({ ...prev, status: 'in_progress' })); inputRef.current?.focus(); }}
                  className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] px-6 text-sm"
                  data-testid="demo-resume-button"
                >
                  Resume
                </Button>
                <Link to="/auth">
                  <Button variant="outline" className="rounded-full border-[#334155] text-[#94A3B8] hover:bg-[#1E293B] px-6 text-sm" data-testid="demo-signup-from-pause">
                    Get Started Free
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSend} className="flex items-center space-x-3">
              <VoiceMode
                sessionId={session?.id}
                chatEndpoint={`${API}/demo/sessions/${session?.id}/chat`}
                onNewMessages={() => session?.id && fetchMessages(session.id)}
                disabled={sending || !session}
              />
              <label htmlFor="demo-input" className="sr-only">Type your response</label>
              <Input
                id="demo-input"
                ref={inputRef}
                type="text"
                placeholder="Type or use voice..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={sending}
                className="flex-1 rounded-full bg-[#1E293B] border-[#334155] text-white placeholder:text-[#64748B] text-sm focus:ring-2 focus:ring-[#3B82F6]"
                data-testid="demo-message-input"
                autoFocus
              />
              <Button
                type="submit"
                disabled={!input.trim() || sending}
                className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] px-5"
                aria-label={sending ? 'Sending' : 'Send message'}
                data-testid="demo-send-button"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
              </Button>
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="text-center py-3 text-xs text-[#475569] border-t border-[#1E293B]">
        Powered by <Link to="/" className="text-[#3B82F6] hover:underline">Evenly Respond</Link>
      </footer>
    </div>
  );
};
